import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface LibraryItem {
  id: string;
  title: string;
  type: 'document' | 'video';
  created_at: string;
  
  // Document specific fields
  name?: string;
  thumbnail_url?: string | null;
  uploadDate?: string;
  size?: string;
  summary?: string;
  status?: string;
  
  // Video specific fields
  video_id?: string;
  embed_url?: string;
  duration?: string;
  channel_title?: string;
  description?: string;
  transcript?: any[];
  chapters?: any[];
  has_chapters?: boolean;
  full_text?: string;
  language?: string;
  total_segments?: number;
  video_info?: any;
}

// Helper function to format file size
const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return 'Unknown size';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {
    return 'Today';
  } else if (diffDays === 2) {
    return 'Yesterday';
  } else if (diffDays <= 7) {
    return `${diffDays - 1} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const useTopicLibraryItems = (topicId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['topic-library-items', topicId],
    queryFn: async (): Promise<LibraryItem[]> => {
      if (!topicId || !user) return [];
      
      // Fetch documents and videos in parallel
      const [documentsResponse, videosResponse] = await Promise.all([
        supabase
          .from('documents')
          .select('id, original_filename, upload_timestamp, file_size_bytes, summary, status, thumbnail_url')
          .eq('topic_id', topicId)
          .eq('user_id', user.id),
        supabase
          .from('topic_videos')
          .select('*')
          .eq('topic_id', topicId)
          .eq('user_id', user.id)
      ]);
      
      if (documentsResponse.error) {
        console.error('Error fetching topic documents:', documentsResponse.error);
        throw documentsResponse.error;
      }
      
      if (videosResponse.error) {
        console.error('Error fetching topic videos:', videosResponse.error);
        throw videosResponse.error;
      }
      
      const documents = documentsResponse.data || [];
      const videos = videosResponse.data || [];
      
      // Transform documents into LibraryItem format
      const documentItems: LibraryItem[] = documents.map(doc => ({
        id: doc.id,
        title: doc.original_filename,
        type: 'document' as const,
        created_at: doc.upload_timestamp,
        name: doc.original_filename,
        thumbnail_url: doc.thumbnail_url,
        uploadDate: formatDate(doc.upload_timestamp),
        size: formatFileSize(doc.file_size_bytes),
        summary: doc.summary || undefined,
        status: doc.status || 'processed',
      }));
      
      // Transform videos into LibraryItem format
      const videoItems: LibraryItem[] = videos.map(video => ({
        id: video.id,
        title: video.title,
        type: 'video' as const,
        created_at: video.created_at,
        video_id: video.video_id,
        embed_url: video.embed_url,
        duration: video.duration,
        channel_title: video.channel_title,
        description: video.description,
        thumbnail_url: video.thumbnail_url,
        transcript: Array.isArray(video.transcript) ? video.transcript : [],
        chapters: Array.isArray(video.chapters) ? video.chapters : [],
        has_chapters: video.has_chapters,
        full_text: video.full_text,
        language: video.language,
        total_segments: video.total_segments,
        video_info: video.video_info,
      }));
      
      // Combine and sort by most recent first
      const allItems = [...documentItems, ...videoItems];
      allItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      return allItems;
    },
    enabled: !!topicId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
